/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  UpdateGatewayInputProps,
  ViewGatewayInputProps
} from "../models/devicelist";
import { DownstreamLinkedDevicesProps } from "../models/linkedDeviceList";

import { ConfigurationVariables } from "../config";

import { AD_ORGANIZATION_ID, RBM_ACCESS_TOKEN_STORAGE_NAME } from "../utils/constants";
import {
  constructDeviceListURL,
  getBearerToken,
  getIAMProjectID
} from "../utils/utility";

export const gateWayManagementAPI = createApi({
  reducerPath: "gateWayManagementAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: ConfigurationVariables.API_BASE_HOST_URL,
    prepareHeaders: async (headers) => {
      try {
        headers.set("authorization", await getBearerToken(RBM_ACCESS_TOKEN_STORAGE_NAME));
        headers.set("organizationId", AD_ORGANIZATION_ID);
        headers.set("IAM-ProjectId", getIAMProjectID());
      } catch (error) {
        console.error(error);
      }

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getDeviceList: builder.query({
      query: ({ limit, pageNumber, filter, filterQuery, sortBy, sortDir }) => ({
        url: constructDeviceListURL(limit, pageNumber, filter, filterQuery, sortBy, sortDir)
      }),
      keepUnusedDataFor: 0
    }),
    getFilterMenu: builder.query({
      query: () => ({
        url: `/v2/projects/${getIAMProjectID()}/devices/filtermenu`
      })
    }),
    getDeviceDetails: builder.query({
      query: ({ deviceId }: ViewGatewayInputProps) =>
        `/v2/projects/${getIAMProjectID()}/device/${deviceId}`,
      keepUnusedDataFor: 0
    }),
    reGenerateRegistrationKey: builder.mutation({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/generateregkey`,
        method: "PATCH"
      })
    }),
    createDevice: builder.mutation({
      query: (device) => ({
        url: `/v2/projects/${getIAMProjectID()}/createdevice`,
        method: "POST",
        body: device // pass the device data
      })
    }),
    activateDevice: builder.mutation({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/activate`,
        method: "POST"
      })
    }),
    deactivateDevice: builder.mutation({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/deactivate`,
        method: "POST"
      })
    }),
    updateGateway: builder.mutation({
      query: (paramObj: UpdateGatewayInputProps) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${paramObj.deviceId}`,
        method: "PUT",
        body: paramObj.gateway
      })
    }),
    deleteDevice: builder.mutation({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/delete`,
        method: "DELETE"
      })
    }),
    revokeAuthenticity: builder.mutation({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/certificate/revoke`,
        method: "POST"
      })
    }),
    getDownstreamDeviceList: builder.query({
      query: ({ deviceId }: DownstreamLinkedDevicesProps) => `v2/projects/${getIAMProjectID()}/device/${deviceId}/downstreamdevices`
    }),
    reRegistrationKey: builder.mutation({
      query: ({ deviceId,registration_key }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${deviceId}/verification`,
        method: "POST",
        body:{registration_key:registration_key}
      })
    }),
    siteBulkAssignment: builder.mutation({
      query: ({ organizationId, organizationName, deviceId, site }) => ({
        url: `/v2/projects/${getIAMProjectID()}/multigatewaysiteassign`,
        method: "POST",
        body:{
          organizationId,
          organizationName,
          deviceId,
          site
        }
      })
    }),
    getHardwareDetails: builder.query({
      query: () => ({
        url: `/v2/projects/${getIAMProjectID()}/gatewaytypedetails`,
        method: "GET"
      })
    }),
    getConnectedDeviceSoftwareVersion: builder.query({
      query: ({ deviceId }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${encodeURIComponent(deviceId)}/software/versions`
      }),
      keepUnusedDataFor: 0
    }),
    getAvailableSoftwareVersions: builder.query({
      query: ({gatewayType, packageType}) => {
        let url = `/v2/projects/${getIAMProjectID()}/${encodeURIComponent(gatewayType)}/software/versions`;
        if (packageType) {
          url += `?packageType=${encodeURIComponent(packageType)}`;
        }
        return { url };
      },
      keepUnusedDataFor: 0
    }),
    getSoftwareVersionDetails: builder.query({
      query: ({gatewayType, version, releaseType}) => {
        let url = `/v2/projects/${getIAMProjectID()}/${encodeURIComponent(gatewayType)}/software/version/${encodeURIComponent(version)}/details`;
        if (releaseType) {
          url += `?release-type=${encodeURIComponent(releaseType)}`;
        }
        return { url };
      },
      keepUnusedDataFor: 0
    }),
    getSoftwareVersionDownloadSasUrl: builder.query({
      query: ({gatewayType, version, releaseType, deviceId}) => {
        let url = `/v2/projects/${getIAMProjectID()}/${encodeURIComponent(gatewayType)}/software/version/${encodeURIComponent(version)}/download`;
        if (releaseType) {
          url += `?release-type=${encodeURIComponent(releaseType)}&deviceId=${encodeURIComponent(deviceId)}`;
        }
        return { url };
      },
      keepUnusedDataFor: 0
    }),
    syncCloudInstall: builder.mutation({
      query: ({ deviceId, version, status = "installed" }) => ({
        url: `/v2/projects/${getIAMProjectID()}/device/${encodeURIComponent(deviceId)}/cloud/install/sync`,
        method: "PATCH",
        body: {
          version,
          status,
        }
      })
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetDeviceListQuery,
  useGetFilterMenuQuery,
  useGetDeviceDetailsQuery,
  useCreateDeviceMutation,
  useUpdateGatewayMutation,
  useActivateDeviceMutation,
  useDeactivateDeviceMutation,
  useReGenerateRegistrationKeyMutation,
  useDeleteDeviceMutation,
  useRevokeAuthenticityMutation,
  useGetDownstreamDeviceListQuery,
  useReRegistrationKeyMutation,
  useSiteBulkAssignmentMutation,
  useGetHardwareDetailsQuery,
  useGetConnectedDeviceSoftwareVersionQuery,
  useGetAvailableSoftwareVersionsQuery,
  useGetSoftwareVersionDetailsQuery,
  useGetSoftwareVersionDownloadSasUrlQuery,
  useSyncCloudInstallMutation
} = gateWayManagementAPI;